import React, { useState, useEffect } from 'react';
import { Typography, Box } from '@mui/material';
import RevisionMetadata from './RevisionMetadata';
import VertexMetadata from './VertexMetadata';
import ServiceHeader from './ServiceHeader';
import { DrawerEmpty, DrawerLoading } from './DrawerStates';
import { useParams } from 'react-router-dom';
import { DRAWER } from '../../../tokens';

// A selectable revision / deployed-model pill (accent underline marks the active one).
function RevisionPill({ primary, traffic, active, onClick }) {
  return (
    <Box
      role="tab"
      aria-selected={active}
      onClick={onClick}
      sx={{
        flexGrow: 1,
        textAlign: 'center',
        cursor: 'pointer',
        userSelect: 'none',
        px: 2,
        pt: 1.5,
        pb: 1,
        color: active ? DRAWER.text : DRAWER.textMuted,
        backgroundColor: active ? 'transparent' : DRAWER.surfaceAlt,
        borderBottom: `2px solid ${active ? DRAWER.accent : 'transparent'}`,
        transition: 'color .15s ease, background-color .15s ease',
        '&:hover': active
          ? {}
          : { color: DRAWER.text, backgroundColor: DRAWER.surfaceAltHover },
      }}
    >
      <Typography sx={{ fontSize: 14, fontWeight: active ? 700 : 500 }}>
        {primary}
      </Typography>
      <Typography
        sx={{
          fontSize: 12,
          fontWeight: 600,
          color: traffic > 0 ? DRAWER.accent : DRAWER.textMuted,
        }}
      >
        ({traffic}%)
      </Typography>
    </Box>
  );
}

function MetadataInfo(props) {
  const {
    node,
    getServiceMetadata,
    loadingServiceMetadata,
    serviceMetadata,
    updateServiceMetadata,
    getVertexMetadata,
    updateVertexMetadata,
    navigationOptions,
  } = props;

  const { session } = useParams();
  const [selectedRevision, setSelectedRevision] = useState('');

  const isCloudRun =
    node.is_vertex === false ||
    node.is_vertex === null ||
    node.is_vertex === undefined;

  useEffect(() => {
    if (isCloudRun) {
      getServiceMetadata({
        service_name: node.service_name,
        project_id: node.project_id,
      });
    } else {
      getVertexMetadata({
        process_id: node.process_id,
        chain: session.split('-')[1].replace(/[^a-zA-Z]+/g, ''),
      });
    }
    return () => {
      setSelectedRevision('');
    };
  }, []);

  const header = <ServiceHeader node={node} />;

  if (!serviceMetadata) {
    return (
      <Box>
        {header}
        <DrawerEmpty>No metadata available for this service.</DrawerEmpty>
      </Box>
    );
  }

  return (
    <Box>
      {header}
      {loadingServiceMetadata ? (
        <DrawerLoading subtext="This may take a few minutes" />
      ) : isCloudRun ? (
        <Box>
          <Box
            sx={{ display: 'flex', borderBottom: `1px solid ${DRAWER.border}` }}
          >
            {serviceMetadata?.revisions?.map((revision, idx) => (
              <RevisionPill
                key={idx}
                primary={revision.revision_id.split('-').slice(-2).join('-')}
                traffic={revision.metadata.traffic}
                active={selectedRevision.revision_id === revision.revision_id}
                onClick={() => setSelectedRevision(revision)}
              />
            ))}
          </Box>

          {serviceMetadata?.revisions?.map((rev) =>
            rev.revision_id === selectedRevision.revision_id ? (
              <RevisionMetadata
                key={rev.revision_id}
                revision={selectedRevision}
                updateServiceMetadata={updateServiceMetadata}
                serviceName={node.service_name}
                projectId={node.project_id}
                navigationOptions={navigationOptions}
              />
            ) : null
          )}
        </Box>
      ) : (
        <Box>
          <Box
            sx={{
              display: 'flex',
              overflow: 'auto',
              borderBottom: `1px solid ${DRAWER.border}`,
            }}
          >
            {serviceMetadata?.deployedModels
              ?.sort((a, b) => new Date(b.createTime) - new Date(a.createTime))
              .map((model, idx) => (
                <RevisionPill
                  key={idx}
                  primary={model.id}
                  traffic={serviceMetadata.trafficSplit[model.id]}
                  active={selectedRevision.id === model.id}
                  onClick={() => setSelectedRevision(model)}
                />
              ))}
          </Box>

          {serviceMetadata?.deployedModels?.map((model) =>
            model.id === selectedRevision.id ? (
              <VertexMetadata
                key={model.id}
                model={selectedRevision}
                trafficSplit={serviceMetadata.trafficSplit}
                updateVertexMetadata={updateVertexMetadata}
                node={node}
                chain={session.split('-')[1].replace(/[^a-zA-Z]+/g, '')}
              />
            ) : null
          )}
        </Box>
      )}
    </Box>
  );
}

export default MetadataInfo;
